import React, { useEffect, useState } from "react";
import { listPrescriptionMedications } from "../services/adminDataService";
import { confirmAction } from "../components/ConfirmationModal";

const APPROVED_PRESCRIPTION_STATUS = "approved_prescription_found";

export const statusLabel = (status) =>
  (status || "pending")
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const displayValue = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  if (typeof value !== "object") return value;
  return value.fullAddress || value.address || value.name || value.email || "-";
};
const priceLabel = (value) => value === "" || value === null || value === undefined ? "—" : `₪${Number(value).toLocaleString("en-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getPatient = (request) => request.patient || {};
const getAddress = (request) => request.address || request.addressDetails || getPatient(request).address || {};
const requestId = (request) => request?.id || request?._id || request?.requestId;

const DetailField = ({ label, value, className = "col-md-6" }) => (
  <div className={className}>
    <p className="small text-muted mb-1">{label}</p>
    <strong className="kupat-detail-value">{displayValue(value)}</strong>
  </div>
);

const KupatHolimReviewModal = ({
  show,
  loading,
  request,
  error,
  saving,
  onClose,
  onSubmit,
}) => {
  const [medications, setMedications] = useState([]);
  const [medicationSearch, setMedicationSearch] = useState("");
  const [selectedPrescriptionItems, setSelectedPrescriptionItems] = useState([]);
  const [medicationLoading, setMedicationLoading] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!show) return;
    setMedications([]);
    setMedicationSearch("");
    setSelectedPrescriptionItems([]);
    setFormError("");
  }, [show, request]);

  useEffect(() => {
    if (!show) return undefined;
    let active = true;
    const loadMedications = async () => {
      try {
        setMedicationLoading(true);
        const items = await listPrescriptionMedications({ search: medicationSearch, page: 1, limit: 50 });
        if (active) setMedications(items.filter((item) => item.isActive !== false));
      } catch (loadError) {
        if (active) setFormError(loadError.message || "Unable to load the prescription medication catalogue.");
      } finally {
        if (active) setMedicationLoading(false);
      }
    };
    const timer = window.setTimeout(loadMedications, 180);
    return () => { active = false; window.clearTimeout(timer); };
  }, [show, medicationSearch]);

  if (!show) return null;

  const isPending = request?.status === "pending";
  const requiresMedicationSelection = true;
  const patient = getPatient(request || {});
  const address = getAddress(request || {});
  const isForSomeoneElse = Boolean(
    request?.isForSomeoneElse === true || request?.requestType === "someone_else" || request?.requestFor === "someone_else",
  );

  const toggleMedication = (medication) => setSelectedPrescriptionItems((items) => {
    const exists = items.some((item) => item.prescriptionMedicationId === medication.id);
    if (exists) return items.filter((item) => item.prescriptionMedicationId !== medication.id);
    return [...items, { prescriptionMedicationId: medication.id, name: medication.name, quantityAllowed: 1, unitPrice: medication.maxConsumerPrice ?? medication.price ?? "" }];
  });
  const adjustPrescriptionItemQuantity = (id, adjustment) => setSelectedPrescriptionItems((items) => items.map((item) => {
    if (item.prescriptionMedicationId !== id) return item;
    return { ...item, quantityAllowed: Math.max(1, (Number(item.quantityAllowed) || 1) + adjustment) };
  }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");
    if (requiresMedicationSelection && selectedPrescriptionItems.length === 0) {
      setFormError("Select at least one prescription medication for a verified prescription.");
      return;
    }
    if (requiresMedicationSelection && selectedPrescriptionItems.some((item) => Number(item.quantityAllowed) < 1 || item.unitPrice === "" || Number(item.unitPrice) < 0)) {
      setFormError("Each selected medication needs a quantity of at least 1 and a valid unit price.");
      return;
    }

    const confirmed = await confirmAction({ title: "Submit final review?", message: "This request cannot be reviewed again after submission.", confirmLabel: "Submit review" });
    if (!confirmed) return;

    const payload = { status: APPROVED_PRESCRIPTION_STATUS };
    if (requiresMedicationSelection) payload.prescriptionItems = selectedPrescriptionItems.map(({ prescriptionMedicationId, quantityAllowed, unitPrice }) => ({ prescriptionMedicationId, quantityAllowed: Number(quantityAllowed), unitPrice: Number(unitPrice) }));
    await onSubmit(requestId(request), payload, setFormError);
  };

  return (
    <>
      <div className="modal show healthid_request" style={{ display: "block" }} tabIndex="-1" role="dialog" aria-modal="true" onClick={(event) => { if (!saving && event.target === event.currentTarget) onClose(); }}>
        <div className="modal-dialog modal-dialog-centered modal-lg kupat-review-dialog">
          <div className="modal-content">
            <div className="modal-header d-flex align-items-center justify-content-between">
              <div>
                <h1 className="modal-title fs-5">Kupat Holim request review</h1>
                {request && <p className="small text-muted mb-0">Request #{requestId(request)} · {formatDate(request.createdAt || request.submittedAt)}</p>}
              </div>
              <button type="button" className="close-btns" onClick={onClose} aria-label="Close review">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>
              </button>
            </div>
            <div className="modal-body kupat-review-body">
              {loading && <div className="py-5 text-center text-muted">Loading request details…</div>}
              {!loading && error && <div className="alert alert-danger">{error}</div>}
              {!loading && request && (
                <>
                  <section className="mb-3 kupat-patient-details">
                    <h6 className="kupat-section-heading">Patient details</h6>
                    <div className="row g-2">
                      <DetailField className="col-md-3" label="Patient name" value={request.patientName || patient.name || patient.fullName} />
                      <DetailField className="col-md-3" label="Request type" value={isForSomeoneElse ? "Someone else" : "Self"} />
                      <DetailField className="col-md-3" label="Teoudat Zehout ID" value={request.teoudatZehoutId || patient.teoudatZehoutId} />
                      <DetailField className="col-md-3" label="Kupat Holim" value={request.kupatHolimId || patient.kupatHolimId} />
                    </div>
                  </section>
                  {Object.keys(address).length > 0 && (
                    <section className="mb-4"><h6 className="kupat-section-heading">Address details</h6><div className="row g-3"><DetailField label="Address" value={address} /><DetailField label="City" value={address.city} /><DetailField label="Postal code" value={address.postalCode || address.zipCode} /><DetailField label="Country" value={address.country} /></div></section>
                  )}
                  <form onSubmit={handleSubmit} className="border-top pt-4">
                    <h6 className="kupat-section-heading">Verified prescription</h6>
                    {!isPending && <div className="alert alert-warning">Review controls are disabled because this request is no longer pending.</div>}
                    {formError && <div className="alert alert-danger">{formError}</div>}
                    <div className="row g-3">
                      {requiresMedicationSelection && <div className="col-12"><label className="form-label" htmlFor="prescriptionMedicationSearch">Prescription medications</label><p className="small text-muted mb-2">Select items from the verified prescription catalogue, then set the allowed quantity. Prices use the catalogue value.</p><input id="prescriptionMedicationSearch" className="form-control mb-2" value={medicationSearch} disabled={!isPending || saving} onChange={(event) => setMedicationSearch(event.target.value)} placeholder="Search prescription medication…" />{medicationLoading ? <p className="small text-muted mb-0">Loading catalogue…</p> : <div className="kupat-medication-picker">{medications.length === 0 ? <p className="small text-muted mb-0">No prescription medications found.</p> : medications.map((medication) => <label key={medication.id} className="kupat-medication-option"><input type="checkbox" checked={selectedPrescriptionItems.some((item) => item.prescriptionMedicationId === medication.id)} disabled={!isPending || saving} onChange={() => toggleMedication(medication)} /><span><strong>{medication.name}</strong><small>{[medication.productCode, medication.packSize].filter(Boolean).join(" · ")}</small></span></label>)}</div>}{selectedPrescriptionItems.length > 0 && <div className="kupat-approved-items">{selectedPrescriptionItems.map((item) => <div className="kupat-approved-item" key={item.prescriptionMedicationId}><strong>{item.name}</strong><div className="kupat-quantity-control"><span>Allowed quantity</span><div><button type="button" onClick={() => adjustPrescriptionItemQuantity(item.prescriptionMedicationId, -1)} disabled={!isPending || saving || Number(item.quantityAllowed) <= 1} aria-label={`Decrease ${item.name} quantity`}>−</button><output aria-label={`${item.name} allowed quantity`}>{item.quantityAllowed}</output><button type="button" onClick={() => adjustPrescriptionItemQuantity(item.prescriptionMedicationId, 1)} disabled={!isPending || saving} aria-label={`Increase ${item.name} quantity`}>+</button></div></div><span className="small text-muted">Catalogue price: {priceLabel(item.unitPrice)}</span></div>)}</div>}<p className="small text-muted mt-2 mb-0">{selectedPrescriptionItems.length} selected</p></div>}
                      <div className="col-12 d-flex justify-content-end gap-2"><button type="button" className="btn btn-outline-secondary" onClick={onClose}>Close</button><button type="submit" className="btn btn-primary" disabled={!isPending || saving}>{saving ? "Submitting…" : "Submit final review"}</button></div>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={saving ? undefined : onClose}></div>
    </>
  );
};

export default KupatHolimReviewModal;
